(function () {
  'use strict';

  /**
   * pp-volume-discount — los RENGLONES de precio por volumen de la ficha
   * (Fase 1A, §28, Dirección 24-oct-2026; estilo Noissue «50 · −48 % ·
   * $2.42/unit»). El marcado lo pinta snippets/pp-volume-discount.liquid con
   * el estado inicial ya correcto; este archivo sólo:
   *
   *   - marca el renglón que rige a la cantidad del selector (is-active +
   *     aria-pressed), venga de un clic, del teclado, de la barra fija del
   *     celular o del carrito;
   *   - al picar un renglón pone su primera cantidad en el selector y dispara
   *     `input` + `change`: pp-main-product.js recalcula el precio y copia la
   *     cantidad a la barra fija (un solo camino, el de siempre);
   *   - llena las TARJETAS (27-oct-2026, Dirección: «hazlo como lo teníamos
   *     antes»; las fichas de antes de #1072): la que ya pagas va en verde
   *     fuerte (is-active) y la que sigue se llena de verde clarito
   *     (`--pp-tier-fill`) conforme avanza la cantidad; arriba, «Agrega N
   *     paquetes más y baja a $X/pieza», o «Con 64 paquetes pagas $X menos
   *     en total» cuando subir al escalón cuesta menos que lo que llevas
   *     (63 a $17,822.70 contra 64 a $17,664: bajar uno y pagar más se
   *     sentía roto);
   *   - avisa `pp:tier-change` { off, qty } con el % REAL del escalón, que es
   *     lo que pp-main-product.js usa para el precio (contrato de siempre);
   *   - repinta % y precio por pieza cuando la ficha le manda
   *     `pp:volumen-precio` { base, compare, sinTachado } (cambio de variante).
   *
   * Fuente de los escalones: `data-pp-rule-tiers` (pp-volume-rules, el mismo
   * dato que cobra la Function del checkout).
   */

  /**
   * % de un escalón dicho contra el TACHADO: el escalón se aplica sobre el
   * precio de oferta, así que con oferta 13 % y escalón 18 % el cliente paga
   * 0.87 × 0.82 = 71 % del tachado → 29 %. Sin tachado (o en variante
   * pactada) el % del escalón ya ES el total. Sólo texto: el cálculo usa
   * data-pp-tier-off, el % real.
   */
  function porcentajeTotalDeEscalon(off, base, compare, sinTachado) {
    if (sinTachado || !compare || compare <= base || !base) return off;
    return Math.round(100 - (base * (100 - off)) / compare);
  }

  /**
   * Precio por pieza de un renglón, con el mismo formato que el Liquid.
   * Regla 26-jun-2026: precio base ≥ $1,000 (100000 centavos) va al peso
   * entero. Con centavos, SIEMPRE dos dígitos ("$1.20", decisión del
   * 8-sep-2026); sin centavos, sin punto ("$6").
   */
  function precioPorPieza(basePrice, unidades, off) {
    var cents = Math.round((basePrice / unidades) * (100 - off) / 100);
    if (basePrice >= 100000) return '$' + Math.round(cents / 100);
    if (cents % 100 === 0) return '$' + (cents / 100).toLocaleString('es-MX');
    var pesos = Math.floor(cents / 100);
    return '$' + pesos.toLocaleString('es-MX') + '.' + (cents / 100).toFixed(2).split('.')[1];
  }

  /**
   * Cuánto se llena cada tramo de la barra con `qty`. `mins` = el primer
   * número de cada escalón, de menos a más. Un tramo ya rebasado va lleno;
   * uno al que no se ha llegado, vacío; el tramo en que va la cantidad se
   * llena en proporción: en «1 a 3» con 1 → 1/3, con 3 → lleno. El último
   * («64 o más») no tiene fin: lleno en cuanto se llega.
   */
  function llenadoDeBarra(qty, mins) {
    var out = [];
    for (var i = 0; i < mins.length; i++) {
      var desde = mins[i];
      var sig = i + 1 < mins.length ? mins[i + 1] : null;
      var f;
      if (qty < desde) f = 0;
      else if (sig === null || qty >= sig) f = 1;
      else f = (qty - desde + 1) / (sig - desde);
      out.push(Math.max(0, Math.min(1, f)));
    }
    return out;
  }

  /**
   * El aviso de arriba de la barra: cuánto falta para el escalón que sigue
   * y a cuánto baja la pieza. En el último escalón, «ya tienes el mejor
   * precio». Las plantillas traen __N__, __NOMBRE__ y __PRECIO__.
   */
  function textoFalta(qty, mins, precios, t) {
    for (var i = 0; i < mins.length; i++) {
      if (qty < mins[i]) {
        var n = mins[i] - qty;
        return t.falta.replace('__N__', n.toLocaleString('es-MX'))
          .replace('__NOMBRE__', n === 1 ? t.uno : t.varios)
          .replace('__PRECIO__', precios[i]);
      }
    }
    return t.tope.replace('__PRECIO__', precios[precios.length - 1]);
  }

  /**
   * Si subir al escalón que sigue cuesta MENOS en total que lo que llevas,
   * cuánto se ahorra (centavos) y con cuántos. `offs` = % real de cada
   * escalón; `base` = precio del paquete en centavos. Si no, null.
   */
  function ahorroAlSubir(qty, mins, offs, base) {
    if (!base) return null;
    var actual = 0;
    for (var i = 0; i < mins.length; i++) if (qty >= mins[i]) actual = i;
    var sig = actual + 1;
    if (sig >= mins.length || qty >= mins[sig]) return null;
    var off = qty >= mins[actual] ? offs[actual] : 0;
    var hoy = Math.round(qty * base * (100 - off) / 100);
    var subiendo = Math.round(mins[sig] * base * (100 - offs[sig]) / 100);
    if (subiendo > hoy) return null;
    return { n: mins[sig], ahorro: hoy - subiendo };
  }

  function dinero(cents) {
    var pesos = Math.floor(cents / 100);
    var c = cents % 100;
    return '$' + pesos.toLocaleString('es-MX') + (c ? '.' + (c < 10 ? '0' : '') + c : '');
  }

  /**
   * Cuánto se llena cada TARJETA: la que sigue a la que rige, en proporción
   * a lo que la cantidad ya avanzó en su tramo (`llenadoDeBarra` del tramo
   * activo); las demás, nada (la activa va en verde fuerte por su clase).
   */
  function llenadoDeTarjetas(qty, mins) {
    var f = llenadoDeBarra(qty, mins);
    var out = mins.map(function () { return 0; });
    var actual = 0;
    for (var i = 0; i < mins.length; i++) if (qty >= mins[i]) actual = i;
    if (actual + 1 < mins.length) out[actual + 1] = f[actual];
    return out;
  }

  function iniciar(root) {
    var renglones = Array.prototype.slice.call(root.querySelectorAll('[data-pp-volumen-renglon]'));
    if (!renglones.length) return;
    var seccion = root.closest('section') || document;
    var qtyInput = seccion.querySelector('[data-pp-qty-input]');
    var unidades = parseInt(root.getAttribute('data-pp-volumen-unidades'), 10) || 1;
    var singular = root.getAttribute('data-pp-volumen-singular') || '';
    var base = parseInt(root.getAttribute('data-pp-volumen-base'), 10) || 0;
    var faltaEl = root.querySelector('[data-pp-volumen-falta-texto]');
    var plantillas = {
      falta: root.getAttribute('data-pp-volumen-falta') || '',
      tope: root.getAttribute('data-pp-volumen-tope') || '',
      ahorro: root.getAttribute('data-pp-volumen-ahorro') || '',
      uno: root.getAttribute('data-pp-volumen-uno') || '',
      varios: root.getAttribute('data-pp-volumen-varios') || ''
    };

    function datos(r) {
      return {
        min: parseInt(r.getAttribute('data-pp-tier-min'), 10) || 1,
        off: parseFloat(r.getAttribute('data-pp-tier-off')) || 0
      };
    }

    function cantidad() {
      if (!qtyInput) return 1;
      var v = parseInt(qtyInput.value, 10);
      return isNaN(v) || v < 1 ? 1 : v;
    }

    // El renglón que rige: el último cuyo mínimo ya se alcanzó (los renglones
    // vienen ordenados de menos a más). Debajo del primero, el primero.
    function renglonDe(qty) {
      var elegido = renglones[0];
      for (var i = 0; i < renglones.length; i++) {
        if (qty >= datos(renglones[i]).min) elegido = renglones[i];
      }
      return elegido;
    }

    function actualizar() {
      var qty = cantidad();
      var activo = renglonDe(qty);
      for (var i = 0; i < renglones.length; i++) {
        var es = renglones[i] === activo;
        renglones[i].classList.toggle('is-active', es);
        renglones[i].setAttribute('aria-pressed', es ? 'true' : 'false');
      }
      // Debajo del primer escalón real no hay descuento aunque el primer
      // renglón exista: su off ya viene en 0, salvo que el mínimo de
      // fabricación caiga arriba de un escalón (entonces sí rige).
      var off = qty >= datos(activo).min ? datos(activo).off : 0;
      pintarBarra(qty);
      root.dispatchEvent(new CustomEvent('pp:tier-change', {
        detail: { off: off, qty: qty },
        bubbles: true
      }));
    }

    function pintarBarra(qty) {
      var mins = renglones.map(function (r) { return datos(r).min; });
      var f = llenadoDeTarjetas(qty, mins);
      for (var i = 0; i < renglones.length; i++) {
        if (f[i] > 0) renglones[i].style.setProperty('--pp-tier-fill', (f[i] * 100).toFixed(1) + '%');
        else renglones[i].style.removeProperty('--pp-tier-fill');
      }
      if (faltaEl && plantillas.falta && plantillas.tope) {
        var offs = renglones.map(function (r) { return datos(r).off; });
        var a = plantillas.ahorro ? ahorroAlSubir(qty, mins, offs, base) : null;
        if (a) {
          faltaEl.textContent = plantillas.ahorro.replace('__N__', a.n.toLocaleString('es-MX'))
            .replace('__NOMBRE__', plantillas.varios)
            .replace('__AHORRO__', dinero(a.ahorro));
        } else {
          var precios = renglones.map(function (r) {
            var e = r.querySelector('[data-pp-volumen-pieza]');
            return e ? e.textContent.trim() : '';
          });
          faltaEl.textContent = textoFalta(qty, mins, precios, plantillas);
        }
      }
    }

    function repintar(nuevaBase, compare, sinTachado) {
      if (!nuevaBase) return;
      base = nuevaBase;
      for (var i = 0; i < renglones.length; i++) {
        var d = datos(renglones[i]);
        var pctEl = renglones[i].querySelector('[data-pp-volumen-pct]');
        var piezaEl = renglones[i].querySelector('[data-pp-volumen-pieza]');
        if (pctEl) {
          var pct = porcentajeTotalDeEscalon(d.off, base, compare, sinTachado);
          pctEl.textContent = pct > 0 ? '-' + pct + '%' : '';
        }
        if (piezaEl) piezaEl.textContent = precioPorPieza(base, unidades, d.off) + '/' + singular;
      }
      pintarBarra(cantidad());
    }

    renglones.forEach(function (r) {
      r.addEventListener('click', function () {
        if (!qtyInput) return;
        qtyInput.value = String(datos(r).min);
        qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    root.addEventListener('pp:volumen-precio', function (e) {
      var d = e.detail || {};
      repintar(d.base, d.compare, d.sinTachado);
    });

    if (qtyInput) {
      qtyInput.addEventListener('input', actualizar);
      qtyInput.addEventListener('change', actualizar);
    }
    actualizar();
  }

  function init() {
    var raices = document.querySelectorAll('[data-pp-volume-discount]');
    for (var i = 0; i < raices.length; i++) iniciar(raices[i]);
  }

  window.PPVolumen = {
    porcentajeTotalDeEscalon: porcentajeTotalDeEscalon,
    precioPorPieza: precioPorPieza,
    llenadoDeBarra: llenadoDeBarra,
    llenadoDeTarjetas: llenadoDeTarjetas,
    ahorroAlSubir: ahorroAlSubir,
    textoFalta: textoFalta
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
